import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useCookiePrefs } from "@/components/CookiePrefsContext";

const FIELDS = [
  { key: "analytics_storage", i18n: "analytics", locked: false },
  { key: "ad_storage", i18n: "ads", locked: false },
  { key: "ad_user_data", i18n: "ad_user", locked: false },
  { key: "ad_personalization", i18n: "ad_personalization", locked: false },
  { key: "personalization_storage", i18n: "personalization", locked: false },
  { key: "functionality_storage", i18n: "functionality", locked: true },
  { key: "security_storage", i18n: "security", locked: true },
];

export default function ManagePreferencesModal() {
  const { t } = useTranslation();
  const { managerOpen, closeManager, state, saveCustom, acceptAll, rejectAll } = useCookiePrefs();
  const [draft, setDraft] = useState(state);

  useEffect(() => {
    setDraft(state);
  }, [state, managerOpen]);

  const toggle = (key) => {
    setDraft((d) => ({
      ...d,
      [key]: d[key] === "granted" ? "denied" : "granted",
    }));
  };

  return (
    <Dialog open={managerOpen} onOpenChange={(v) => (!v ? closeManager() : null)}>
      <DialogContent
        className="max-w-[860px] rounded-2xl"
        data-testid="manage-preferences-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[-0.01em]">
            {t("cookie.modal_title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("cookie.modal_intro")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 divide-y divide-border">
          {FIELDS.map(({ key, i18n, locked }) => {
            const checked = draft[key] === "granted";
            return (
              <div key={key} className="py-4 flex items-start gap-4">
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">
                    {t(`cookie.categories.${i18n}.title`)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t(`cookie.categories.${i18n}.desc`)}
                  </div>
                </div>
                <div className="pt-0.5 flex items-center gap-2">
                  {locked ? (
                    <span className="text-[0.68rem] font-mono-plex tracking-[0.14em] uppercase text-muted-foreground">
                      {t("cookie.always_on")}
                    </span>
                  ) : null}
                  <Switch
                    checked={locked ? true : checked}
                    disabled={locked}
                    onCheckedChange={() => !locked && toggle(key)}
                    data-testid={`consent-toggle-${key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={rejectAll}
            data-testid="modal-reject-all"
            className="h-10 rounded-lg"
          >
            {t("cookie.reject_all")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={acceptAll}
            data-testid="modal-accept-all"
            className="h-10 rounded-lg"
          >
            {t("cookie.accept_all")}
          </Button>
          <Button
            type="button"
            onClick={() => saveCustom(draft)}
            data-testid="cookie-preferences-save"
            className="h-10 rounded-lg"
          >
            {t("cookie.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
